/**
 * Suspense Wrapper - Loading fallback for lazy components
 * Provides consistent loading states for code-split components
 */

import React, { Suspense } from "react";
import Loading from "../../shared/ui/Loading";

/**
 * SuspenseWrapper - Wraps lazy components with loading fallback
 * @param {Object} props
 * @param {React.ReactNode} props.children - Lazy component to wrap
 * @param {React.ReactNode} props.fallback - Custom fallback component
 * @param {string} props.message - Loading message
 */
const SuspenseWrapper = ({
  children,
  fallback = null,
  message = "Loading...",
}) => {
  const defaultFallback = (
    <div className="lazy-loading">
      <Loading message={message} />
    </div>
  );

  return <Suspense fallback={fallback || defaultFallback}>{children}</Suspense>;
};

/**
 * RouteSuspense - Specialized suspense wrapper for route components
 * @param {Object} props
 * @param {React.ReactNode} props.children - Route component
 */
export const RouteSuspense = ({ children }) => (
  <SuspenseWrapper
    message="Loading page..."
    fallback={
      <div className="route-loading">
        <Loading message="Loading page..." fullscreen />
      </div>
    }
  >
    {children}
  </SuspenseWrapper>
);

/**
 * ComponentSuspense - Specialized suspense wrapper for heavy components
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to wrap
 * @param {string} props.message - Loading message
 */
export const ComponentSuspense = ({
  children,
  message = "Loading component...",
}) => <SuspenseWrapper message={message}>{children}</SuspenseWrapper>;

export default SuspenseWrapper;
